import logger from "@airhelp/node-logger";
import { Flex, Heading, ModalCloseButton, VStack } from "@chakra-ui/react";
import {
  FormError,
  FormSubmitButton,
  FormSuccessMessage,
  Input,
  SelectCountries,
  Terms,
  Textarea,
} from "components/FormControl";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { AgreeWithTerms, BecomeAPartnerFormFields } from "services/registerPartner";
import { registerPartner } from "services/registerPartner";
import { getPageUrl } from "utils/getPageUrl";
import { trackCustomEvent } from "utils/tracking";

const SUBSCRIBE_EVENT = { event: "submit_contact", label: "form_submission", type: "become_a_partner" } as const;

export const BecomeAPartnerForm = () => {
  const translations = useTranslations();
  const language = useLanguage();
  const { handleSubmit, errors, register, formState, watch } = useForm<BecomeAPartnerFormFields & AgreeWithTerms>();
  const [formError, setFormError] = useState(false);
  const wasFormSubmittedSucessfully = formState.isSubmitSuccessful && !formError;
  const watchTermsAccepted = watch("agreeWithTerms", false);

  const handleOnSubmit = async (data: BecomeAPartnerFormFields & AgreeWithTerms) => {
    const {
      company,
      fullName,
      role,
      email,
      phone,
      address,
      city,
      country,
      companyBusiness,
      flightTickets,
      percentageOfTickets,
      flightCompensations,
      other,
    } = data;

    try {
      await registerPartner(
        {
          company,
          fullName,
          role,
          email,
          phone,
          address,
          city,
          country,
          companyBusiness,
          flightTickets,
          percentageOfTickets,
          flightCompensations,
          other,
        },
        "becomePartnerSpreadsheet"
      );
      setFormError(false);
      trackCustomEvent(SUBSCRIBE_EVENT);
    } catch (error) {
      setFormError(true);
      logger.error("Error while Register Legal Partner", error);
    }
  };

  return (
    <Flex flexDir="column" p={{ base: 8, lg: 10 }}>
      <Heading as="h3" variant="h3" mb={6}>
        {translations("become_partner_form")}
      </Heading>
      <ModalCloseButton size="md" color="primary.900" />
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <VStack spacing={6} align="left">
          <Input
            name="company"
            errors={errors}
            label={translations("become_partner_form_company")}
            disabled={wasFormSubmittedSucessfully}
            register={register({ required: true, maxLength: 50 })}
          />
          <Input
            name="fullName"
            errors={errors}
            label={translations("become_partner_form_full_name")}
            disabled={wasFormSubmittedSucessfully}
            register={register({ required: true, maxLength: 50 })}
          />
          <Input
            name="role"
            errors={errors}
            label={translations("become_partner_form_role")}
            disabled={wasFormSubmittedSucessfully}
            register={register({ maxLength: 50 })}
          />
          <Input
            name="email"
            type="email"
            errors={errors}
            label={translations("email_address")}
            disabled={wasFormSubmittedSucessfully}
            register={register({ required: true, maxLength: 50 })}
          />
          <Input
            type="tel"
            errors={errors}
            name="phone"
            label={translations("form_phone_number")}
            register={register({ maxLength: 20 })}
            disabled={wasFormSubmittedSucessfully}
          />
          <Input
            errors={errors}
            name="address"
            label={translations("form_address")}
            register={register({ maxLength: 50 })}
            disabled={wasFormSubmittedSucessfully}
          />
          <Input
            errors={errors}
            name="city"
            label={translations("form_city")}
            register={register({ maxLength: 50 })}
            required
            disabled={wasFormSubmittedSucessfully}
          />
          <SelectCountries
            errors={errors}
            name="country"
            label={translations("form_country")}
            placeholder={translations("form_country")}
            register={register()}
            disabled={wasFormSubmittedSucessfully}
          />
          <Textarea
            errors={errors}
            name="companyBusiness"
            label={translations("become_partner_form_main_business")}
            register={register({ maxLength: 1000 })}
            disabled={wasFormSubmittedSucessfully}
            mb={0}
          />
          <Input
            errors={errors}
            name="flightTickets"
            label={translations("become_partner_form_flight_tickets")}
            register={register({ maxLength: 50 })}
            disabled={wasFormSubmittedSucessfully}
          />
          <Input
            errors={errors}
            name="percentageOfTickets"
            label={translations("become_partner_form_percentage_tickets")}
            register={register({ maxLength: 50 })}
            disabled={wasFormSubmittedSucessfully}
          />
          <Textarea
            errors={errors}
            name="flightCompensations"
            label={translations("become_partner_form_flight_compensations")}
            register={register({ maxLength: 1000 })}
            disabled={wasFormSubmittedSucessfully}
            mb={0}
          />
          <Textarea
            errors={errors}
            name="other"
            label={translations("become_partner_form_other")}
            register={register({ maxLength: 1000 })}
            disabled={wasFormSubmittedSucessfully}
            mb={0}
          />
          <Terms
            register={register({ required: true })}
            isDisabled={wasFormSubmittedSucessfully}
            htmlContent={translations("form_agree_to_privacy", {
              "%privacy-link-attrs%": `href="${getPageUrl(language, "privacy")}"`,
            })}
          />
          {formError ? (
            <FormError
              formError={formError}
              formErrorText={translations("subscribe_form_submit_error")}
              id="form-error"
            />
          ) : null}
          {wasFormSubmittedSucessfully ? (
            <FormSuccessMessage text={translations("form_submit_success")} />
          ) : (
            <FormSubmitButton
              text={translations("become_partner_form")}
              isDisabled={!watchTermsAccepted}
              isLoading={formState.isSubmitting}
              mt={4}
              width="full"
              data-testid="form-submit-button"
            />
          )}
        </VStack>
      </form>
    </Flex>
  );
};
